import { WorkflowName } from "../../../apis/API";

/**
 * HTML autofill tokens for workflow fields — WCAG 2.1 SC 1.3.5 Identify Input
 * Purpose.
 *
 * 1.3.5 covers **the user's own information**, so a token belongs on a field
 * only when the participant is being asked about themselves. Putting one on a
 * field that asks about someone else is worse than omitting it: it invites the
 * browser to autofill the participant's details into a field describing their
 * employer, their emergency contact, or a vehicle's registered owner.
 *
 * The lookup is keyed on `(workflowName, fieldKey)` and NOT on the field key
 * alone, because `streetAddress`, `city`, `state` and `zipCode` each appear on
 * two workflows with different owners:
 *
 *   - `addressConfirmation` — the participant's own home address  → tokens
 *   - `employment`          — their employer's address            → no tokens
 *
 * (`getFieldInputHints` in `fieldValidation.ts` may key on the field key alone
 * because each of its three validation keys is unique to a single workflow.
 * That is not true here; do not copy the pattern.)
 *
 * Workflows deliberately absent from this table, with the reason:
 *   - `emergencyContact` (name, phone)      — a third party's contact details
 *   - `employment` (employer address, etc.) — the employer's details
 *   - `vehicles` (ownerName, plateNum, …)   — the registered owner's details
 *   - everything else                       — not personal-identity data
 */

/**
 * Autofill tokens by workflow, then by field key.
 *
 * Typed on `WorkflowName` so a workflow rename in `workflowRegistry.ts` breaks
 * the build here instead of silently dropping the tokens. Field keys cannot be
 * typed the same way (the registry builds them from string literals), so
 * `autocompleteTokens.test.ts` asserts every key below still exists on its
 * workflow's spec — a field rename would otherwise drift unnoticed.
 */
const TOKENS: Partial<Record<WorkflowName, Record<string, string>>> = {
  // The participant's own email — its own CONFIRM_ON_FILE workflow
  // ("Do you have an email address?"), not a field on `vehicles`.
  email: {
    email: "email",
  },
  // The participant's own phone numbers. `other` is deliberately omitted: the
  // registry does not say whose second number it is, and a wrong token here
  // would autofill the participant's number into someone else's field.
  phoneConfirmation: {
    mobile: "tel",
  },
  // The participant's own home address.
  addressConfirmation: {
    streetAddress: "address-line1",
    city: "address-level2",
    state: "address-level1",
    zipCode: "postal-code",
    // NOTE: `country` is deliberately absent. It is in CARRIED_FIELDS — it
    // rides through the answer payload but renders no input — so a token here
    // could never apply. It would also be the wrong one: the value is an ISO
    // 3166 code ("US"), which is `country`, not `country-name`.
  },
};

/**
 * The autofill token for a workflow field, or `undefined` when the field is not
 * the participant's own information. `undefined` is the correct, deliberate
 * answer for every third-party field — see the module comment.
 */
export function getAutocompleteToken(
  workflowName: string | undefined,
  fieldKey: string,
): string | undefined {
  if (!workflowName) return undefined;
  // hasOwnProperty on both levels, matching `lookupWorkflow`: a raw index would
  // resolve inherited Object.prototype keys, so getAutocompleteToken(
  // "constructor", "name") would return "Object" as if it were a token.
  if (!Object.prototype.hasOwnProperty.call(TOKENS, workflowName)) {
    return undefined;
  }
  const fields = TOKENS[workflowName as WorkflowName] as Record<string, string>;
  if (!Object.prototype.hasOwnProperty.call(fields, fieldKey)) {
    return undefined;
  }
  return fields[fieldKey];
}
